import { Direction } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { ThreadReadStatus } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { SMSDataSharedUtils } from '@frontend/api-sms-data';
import { SchemaIO } from '@frontend/schema';
import { useGetUnifiedThreadEnabled } from '../../../utils';
import { useSMSDataV4QueryUpdaters, useUnifiedThreadUpdater } from '../../../v4/query-updaters';
import { useSMSDataV3QueryUpdaters } from '../../query-updaters';
import { ServiceMutations } from '../../types';
import { UseSMSDataV3MutationEndpointArgs, useSMSDataV3Mutation } from '../use-sms-data-v3-mutation';

/**
 * A hook that returns a mutation for the `SetThreadsStatusRead` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useSetThreadsStatusReadMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends { automatedThreadIds: string[]; manualThreadIds: string[] } = {
    automatedThreadIds: string[];
    manualThreadIds: string[];
  },
  RequestOverride extends SchemaIO<ServiceMutations['SetThreadsStatusRead']>['input'] = SchemaIO<
    ServiceMutations['SetThreadsStatusRead']
  >['input']
>({
  options,
  ...args
}: UseSMSDataV3MutationEndpointArgs<'SetThreadsStatusRead', E, C | undefined, OtherOptions, RequestOverride> = {}) => {
  const { updateThread } = useSMSDataV3QueryUpdaters();
  const v4QueryUpdaters = useSMSDataV4QueryUpdaters({ httpOptions: args.httpOptions });
  const unifiedThreadUpdater = useUnifiedThreadUpdater({ httpOptions: args.httpOptions });
  const { isSmsMessageItem } = SMSDataSharedUtils.threadItemsUtils;
  const getUnifiedThreadEnabled = useGetUnifiedThreadEnabled();

  return useSMSDataV3Mutation<'SetThreadsStatusRead', E, C, OtherOptions, RequestOverride>({
    endpointName: 'SetThreadsStatusRead',
    ...args,
    options: {
      ...options,
      onSuccess: (response, request, context) => {
        response.succeeded.forEach((threadId) => {
          if (getUnifiedThreadEnabled([request.locationId])) {
            unifiedThreadUpdater.upsertUnifiedThread({
              matchValues: {
                threadId: threadId,
                locationId: request.locationId,
              },
              updater: (oldThreadData) => {
                if (oldThreadData) {
                  // Determine isReplied based on the first message's direction
                  const firstItem = oldThreadData.items?.[0];
                  const firstSms = firstItem && isSmsMessageItem(firstItem) ? firstItem.smsMessage : undefined;
                  const shouldBeReplied =
                    firstSms?.direction === Direction.DIRECTION_OUTBOUND && !firstSms?.autogeneratedBy;

                  return {
                    ...oldThreadData,
                    status: ThreadReadStatus.THREAD_READ_STATUS_READ,
                    isReplied: shouldBeReplied,
                  };
                }
                return oldThreadData;
              },
              isOptimistic: false,
            });
          } else {
            updateThread({
              matchValues: {
                id: threadId,
                groupIds: request.groupIds,
                locationId: request.locationId,
              },
              newValues: {
                status: 'read',
              },
            });
            v4QueryUpdaters.upsertThread({
              matchValues: {
                threadId,
                locationId: request.locationId,
              },
              matchFn: (thread) => thread.id === threadId,
              matchStrategy: 'or',
              updater: (thread) => (thread ? { ...thread, status: 'read' } : thread),
              isOptimistic: false,
            });
          }
        });

        return options?.onSuccess?.(response, request, context);
      },
    },
  });
};
